import { useAppTheme } from "@hooks/useAppTheme";
import { Box, Button, TooltipWrapper } from "@memorang/ui";
import { useTranslation } from "react-i18next";
import { Platform, type ViewStyle } from "react-native";

// Paper's disabled TouchableRipple forces cursor: "auto" on web, which blocks
// the tooltip trigger's "help" cursor from inheriting. react-native-web
// renders any CSS cursor; RN's types only model "auto" | "pointer".
const helpCursorStyle =
	Platform.OS === "web"
		? ({ cursor: "help" } as unknown as ViewStyle)
		: undefined;

/* -----------------Props--------------- */
export type NextButtonProps = {
	handlePrevious: () => void;
	handleNext: () => void;
	showButtonPrevious?: boolean;
	showButtonNavSkip?: boolean;
	showButtonNavNext?: boolean;
	showButtonNavFinish?: boolean;
	disableNextButton?: boolean;
	// Shown on hover/focus over the Next button while it is disabled, to
	// explain what must be completed before continuing.
	disabledNextTooltip?: string;
	disablePreviousButton?: boolean;
	nextButtonText?: string;
	prevButtonText?: string;
	progressText?: string;
	isSummativeTest?: boolean;
	toggleHelpDialog?: () => void;
	inReviewMode?: boolean;
};

/* -----------------Component--------------- */
const NavButtons = ({
	handlePrevious,
	handleNext,
	showButtonPrevious,
	showButtonNavSkip,
	showButtonNavNext,
	showButtonNavFinish,
	disableNextButton,
	disabledNextTooltip,
	disablePreviousButton,
	nextButtonText,
	prevButtonText,
	isSummativeTest,
	inReviewMode,
	toggleHelpDialog,
}: NextButtonProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const roundedNavButtonStyle = {
		borderRadius: 999,
		overflow: "hidden" as const,
	};

	const nextButtonMode =
		showButtonNavSkip || disableNextButton
			? "outlined"
			: isSummativeTest
				? "elevated"
				: "contained";

	const getNextButtonNext = () => {
		switch (true) {
			case showButtonNavSkip:
				return t("session.navigation.skip");
			case showButtonNavFinish:
				return t("session.navigation.finish");
			case showButtonNavNext:
				return nextButtonText || t("session.navigation.next");
			default:
				return t("session.navigation.finish");
		}
	};

	const nextButtonIcon = showButtonNavSkip
		? "skip-next-outline"
		: "chevron-right";

	const PreviousButton = () => {
		return (
			<>
				{showButtonPrevious ? (
					<Button
						mode="outlined"
						textColor={isSummativeTest ? theme.colors.onSurface : undefined}
						icon={"chevron-left"}
						style={{
							...roundedNavButtonStyle,
							borderColor: theme.colors.divider,
						}}
						onPress={handlePrevious}
						disabled={disablePreviousButton}
					>
						{prevButtonText || t("session.navigation.previous")}
					</Button>
				) : (
					<Box width={96} />
				)}
			</>
		);
	};

	const getNextButtonBackgroundColor = () => {
		if (isSummativeTest) {
			if (showButtonNavSkip) {
				return "transparent";
			}
			return theme.colors.primary;
		}
		return showButtonNavSkip ? "transparent" : theme.colors.primary;
	};

	const bg = getNextButtonBackgroundColor();
	const NextButton = () => {
		return (
			<Button
				onPress={handleNext}
				mode={nextButtonMode}
				style={{
					...roundedNavButtonStyle,
					borderColor: theme.colors.divider,
					backgroundColor: disableNextButton ? theme.colors.backdrop : bg,
				}}
				textColor={
					isSummativeTest
						? showButtonNavSkip
							? theme.colors.onSurface
							: theme.colors.onPrimary
						: undefined
				}
				icon={nextButtonIcon}
				contentStyle={[
					{ flexDirection: "row-reverse" },
					disableNextButton && disabledNextTooltip ? helpCursorStyle : null,
				]}
				disabled={disableNextButton}
			>
				{getNextButtonNext()}
			</Button>
		);
	};

	const nextButton =
		disableNextButton && disabledNextTooltip ? (
			<TooltipWrapper title={disabledNextTooltip}>
				<NextButton />
			</TooltipWrapper>
		) : (
			<NextButton />
		);

	const SummativeTestNavButtons = () => {
		return (
			<Box flexDirection="row" gap={16}>
				<PreviousButton />
				{nextButton}
			</Box>
		);
	};

	const FormativeTestNavButtons = () => {
		return (
			<>
				<PreviousButton />
				{nextButton}
			</>
		);
	};

	return (
		<Box
			flexDirection="row"
			justifyContent="space-between"
			alignItems="center"
			width="100%"
		>
			{isSummativeTest &&
				(!inReviewMode && toggleHelpDialog ? (
					<Button
						icon={"help-circle-outline"}
						textColor={theme.colors.onSurface}
						onPress={toggleHelpDialog}
					>
						{t("session.navigation.help")}
					</Button>
				) : (
					// Keep space-between balanced so the nav group stays right-aligned
					// when there is no Help button.
					<Box />
				))}
			{isSummativeTest ? (
				<SummativeTestNavButtons />
			) : (
				<FormativeTestNavButtons />
			)}
		</Box>
	);
};

export default NavButtons;
